// Magic Forms, from any page
import { buildFormTools, buildFormGuideTool, type PendingFormAction } from "./tools"
import { buildFormSystemPrompt, FORMS_BINDING, FORMS_ELSEWHERE } from "./prompt"
import type { AssistantModule, ApprovalRequest, ModuleCatalogEntry, ToolPolicy } from "@/lib/assistant/types"

export const formsCatalog: ModuleCatalogEntry = {
  id: "forms",
  title: "Magic Forms",
  route: "/evaluations/forms",
  description: "Always available, from any page: list, create and edit Magic Forms and their fields.",
  resource: "forms",
}

// Other parts of the app listen for this to refresh their form lists
export const FORMS_CHANGED_EVENT = "assistant:forms-changed"

const KIND_LABEL: Record<PendingFormAction["kind"], string> = {
  update_form: "Replace form",
  add_field: "Add field",
  update_field: "Update field",
  remove_field: "Remove field",
  delete_form: "Delete form",
}

export function makeFormsModule(ctx: {
  teamId?: string
  onFormsPage: boolean
  ask: (req: ApprovalRequest) => Promise<boolean>
}): AssistantModule {
  const tools = {
    ...buildFormTools({
      teamId: ctx.teamId,
      onFormsChanged: () => window.dispatchEvent(new Event(FORMS_CHANGED_EVENT)),
      onApprove: (action: PendingFormAction) => ctx.ask({
        summary: `${KIND_LABEL[action.kind] ?? action.kind}: ${action.formTitle} (${action.summary})`,
        detail: action.detail ? JSON.stringify(action.detail, null, 2) : undefined,
      }),
    }),
    ...buildFormGuideTool(),
  }

  const read = (resource = "forms"): ToolPolicy => ({ kind: "read", permission: [resource, "read"] })
  const edit: ToolPolicy = { kind: "write", permission: ["forms", "update"], selfReviewed: true }
  const policies: Record<string, ToolPolicy> = {
    list_forms: read(),
    get_form: read(),
    forms_guide: read(),
    list_agents: read("agents"),
    get_agent_details: read("agents"),
    create_form: { kind: "write", permission: ["forms", "create"] },
    update_form: edit,
    add_field: edit,
    update_field: edit,
    remove_field: edit,
    reorder_fields: edit,
    delete_form: { kind: "destructive", permission: ["forms", "delete"], selfReviewed: true },
  }

  return {
    id: "forms",
    title: "Magic Forms",
    tools,
    policies,
    instructions: `${ctx.onFormsPage ? buildFormSystemPrompt() : FORMS_ELSEWHERE}\n\n${FORMS_BINDING}`,
    labels: {
      list_forms: "List forms", get_form: "Read form", forms_guide: "Read forms guide",
      list_agents: "List agents", get_agent_details: "Read agent",
      create_form: "Create form", update_form: "Replace form", add_field: "Add field",
      update_field: "Update field", remove_field: "Remove field", reorder_fields: "Reorder fields",
      delete_form: "Delete form",
    },
  }
}
